import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Alert,
    CircularProgress,
    ToggleButtonGroup,
    ToggleButton,
    Typography,
    Paper,
    Stack,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Switch,
    IconButton,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import ProjectPicker from "../../pickers/ProjectPicker";
import QuotePicker from "../../pickers/QuotePicker";
import EmployeePicker from "../../pickers/EmployeePicker";
import EngineeringService from "../../../services/Engineering.service";

const AFFECTS_FIELDS = [
    { key: "affects_cost", label: "Cost" },
    { key: "affects_schedule", label: "Schedule" },
    { key: "affects_procurement", label: "Procurement" },
    { key: "affects_installation", label: "Installation" },
    { key: "affects_fabrication", label: "Fabrication" },
];

const EMPTY_FORM = {
    linkedTo: "project",
    project: null,
    quote: null,
    title: "",
    is_external: true,
    affects_cost: false,
    affects_schedule: false,
    affects_procurement: false,
    affects_installation: false,
    affects_fabrication: false,
    description: "",
    reason_for_change: "",
    comments: "",
    requested_by: null,
    issued_by: null,
    approved_by: null,
};

export default function ECNFormDialog({ open, onClose, item, onSaved }) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const [form, setForm] = useState({ ...EMPTY_FORM });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const isEditing = Boolean(item);

    useEffect(() => {
        if (!open) return;
        if (item) {
            setForm({
                linkedTo: item.is_generic
                    ? "generic"
                    : item.project_detail
                        ? "project"
                        : "quote",
                project: item.project_detail
                    ? { id: item.project, ...item.project_detail }
                    : null,
                quote: item.quote_detail
                    ? { id: item.quote, ...item.quote_detail }
                    : null,
                title: item.title || "",
                is_external: item.is_external,
                affects_cost: item.affects_cost,
                affects_schedule: item.affects_schedule,
                affects_procurement: item.affects_procurement,
                affects_installation: item.affects_installation,
                affects_fabrication: item.affects_fabrication,
                description: item.description || "",
                reason_for_change: item.reason_for_change || "",
                comments: item.comments || "",
                requested_by: item.requested_by_detail || null,
                issued_by: item.issued_by_detail || null,
                approved_by: item.approved_by_detail || null,
            });
        } else {
            setForm({ ...EMPTY_FORM });
        }
        setError("");
    }, [open, item]);

    const set = (field) => (value) =>
        setForm((f) => ({ ...f, [field]: value }));

    const handleLinkedToChange = (_, val) => {
        if (!val) return;
        setForm((f) => ({ ...f, linkedTo: val, project: null, quote: null }));
    };

    const handleAffectsChange = (key) => (e) =>
        setForm((f) => ({ ...f, [key]: e.target.checked }));

    const handleSave = async () => {
        if (!form.title.trim()) {
            setError("Title is required.");
            return;
        }
        if (form.linkedTo === "project" && !form.project) {
            setError("Please select a project.");
            return;
        }
        if (form.linkedTo === "quote" && !form.quote) {
            setError("Please select a quote.");
            return;
        }

        const payload = {
            title: form.title,
            is_external: form.is_external,
            is_generic: form.linkedTo === "generic",
            affects_cost: form.affects_cost,
            affects_schedule: form.affects_schedule,
            affects_procurement: form.affects_procurement,
            affects_installation: form.affects_installation,
            affects_fabrication: form.affects_fabrication,
            description: form.description,
            reason_for_change: form.reason_for_change,
            comments: form.comments,
            requested_by: form.requested_by?.id || null,
            issued_by: form.issued_by?.id || null,
            approved_by: form.approved_by?.id || null,
            project: form.linkedTo === "project" ? form.project?.id : null,
            quote: form.linkedTo === "quote" ? form.quote?.id : null,
        };

        setLoading(true);
        setError("");
        try {
            if (isEditing) {
                await EngineeringService.updateECN(item.id, payload);
            } else {
                await EngineeringService.createECN(payload);
            }
            onSaved();
            onClose();
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                JSON.stringify(err?.response?.data) ||
                "An error occurred.";
            setError(detail);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            fullScreen={fullScreen}
        >
            <DialogTitle>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    {isEditing ? "Edit ECN" : "New ECN"}
                    {fullScreen && (
                        <IconButton size="small" onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    )}
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    {error && (
                        <Grid item xs={12}>
                            <Alert severity="error">{error}</Alert>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                            Link to
                        </Typography>
                        <ToggleButtonGroup
                            value={form.linkedTo}
                            exclusive
                            onChange={handleLinkedToChange}
                            size="small"
                            fullWidth
                            sx={{ display: "flex", mt: 0.5 }}
                        >
                            <ToggleButton value="project" sx={{ flex: 1 }}>
                                Project
                            </ToggleButton>
                            <ToggleButton value="quote" sx={{ flex: 1 }}>
                                Quote
                            </ToggleButton>
                            <ToggleButton value="generic" sx={{ flex: 1 }}>
                                Generic
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>

                    {form.linkedTo !== "generic" && (
                        <Grid item xs={12}>
                            {form.linkedTo === "project" ? (
                                <ProjectPicker
                                    value={form.project}
                                    onChange={set("project")}
                                    label="Project"
                                />
                            ) : (
                                <QuotePicker
                                    value={form.quote}
                                    onChange={set("quote")}
                                    label="Quote"
                                />
                            )}
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <TextField
                            label="Title"
                            value={form.title}
                            onChange={(e) => set("title")(e.target.value)}
                            fullWidth
                            inputProps={{ maxLength: 255 }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.is_external}
                                    onChange={(e) =>
                                        set("is_external")(e.target.checked)
                                    }
                                />
                            }
                            label={form.is_external ? "External" : "Internal"}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography
                                variant="overline"
                                color="text.secondary"
                                display="block"
                                mb={0.5}
                            >
                                Affects
                            </Typography>
                            <FormGroup>
                                {AFFECTS_FIELDS.map(({ key, label }) => (
                                    <FormControlLabel
                                        key={key}
                                        control={
                                            <Checkbox
                                                checked={form[key]}
                                                onChange={handleAffectsChange(key)}
                                            />
                                        }
                                        label={label}
                                    />
                                ))}
                            </FormGroup>
                        </Paper>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Description"
                            value={form.description}
                            onChange={(e) => set("description")(e.target.value)}
                            multiline
                            minRows={3}
                            fullWidth
                            inputProps={{ maxLength: 5000 }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Reason for Change"
                            value={form.reason_for_change}
                            onChange={(e) =>
                                set("reason_for_change")(e.target.value)
                            }
                            multiline
                            minRows={3}
                            fullWidth
                            inputProps={{ maxLength: 3000 }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Comments"
                            value={form.comments}
                            onChange={(e) => set("comments")(e.target.value)}
                            multiline
                            minRows={2}
                            fullWidth
                            inputProps={{ maxLength: 1000 }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <EmployeePicker
                            label="Requested By"
                            value={form.requested_by}
                            onChange={set("requested_by")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <EmployeePicker
                            label="Issued By"
                            value={form.issued_by}
                            onChange={set("issued_by")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <EmployeePicker
                            label="Approved By"
                            value={form.approved_by}
                            onChange={set("approved_by")}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions
                sx={
                    fullScreen
                        ? { pb: "max(8px, env(safe-area-inset-bottom))" }
                        : undefined
                }
            >
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} /> : null}
                >
                    {isEditing ? "Save Changes" : "Create ECN"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
